'use client';
import React, { useState } from 'react';
import TaxpayerProfileHeader from './TaxpayerProfileHeader';
import ObligationCards from './ObligationCards';
import PaymentHistoryTable from './PaymentHistoryTable';
import FilingStatusTracker from './FilingStatusTracker';
import DocumentDownloads from './DocumentDownloads';
import DocumentUploadTab from './DocumentUploadTab';
import QuickPayModal from './QuickPayModal';
import LRARealPropertyTaxInfo from './LRARealPropertyTaxInfo';
import PropertyDetailsPanel from './PropertyDetailsPanel';

export type ActiveTab = 'overview' | 'payments' | 'filings' | 'documents' | 'upload-docs';

export default function TaxpayerPortalClient() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [selectedObligation, setSelectedObligation] = useState<string | null>(null);

  const tabs: { key: ActiveTab; label: string; badge?: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'payments', label: 'Payment History', badge: '3' },
    { key: 'filings', label: 'Tax Filings', badge: '2' },
    { key: 'documents', label: 'Documents & Certificates' },
    { key: 'upload-docs', label: 'Upload Documents', badge: 'NEW' },
  ];

  const openPayModal = (obligationId: string) => {
    setSelectedObligation(obligationId);
    setPayModalOpen(true);
  };

  return (
    <div
      className="min-h-screen px-4 py-5 lg:px-6 xl:px-8 2xl:px-10 max-w-screen-2xl mx-auto"
      style={{ backgroundColor: 'var(--dark-bg)' }}
    >
      <TaxpayerProfileHeader />

      {/* Registered Property Details — government-grade */}
      <PropertyDetailsPanel />

      {/* Obligation cards */}
      <ObligationCards onPay={openPayModal} />

      {/* LRA Real Property Tax Info */}
      <div className="mt-5">
        <LRARealPropertyTaxInfo />
      </div>

      {/* Tabs */}
      <div
        className="mt-5 rounded-xl overflow-hidden"
        style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
      >
        <div
          className="flex border-b overflow-x-auto scrollbar-thin"
          style={{ borderColor: 'var(--dark-border)' }}
        >
          {tabs.map((tab) => (
            <button
              key={`tab-${tab.key}`}
              onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-all duration-150 border-b-2"
              style={{
                borderColor: activeTab === tab.key ? 'var(--accent)' : 'transparent',
                color: activeTab === tab.key ? 'var(--accent)' : 'var(--dark-muted)',
                backgroundColor: activeTab === tab.key ? 'rgba(212,160,23,0.05)' : 'transparent',
              }}
            >
              {tab.label}
              {tab.badge && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                  style={{
                    backgroundColor: 'rgba(212,160,23,0.2)',
                    color: 'var(--accent)',
                  }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-5">
          {activeTab === 'overview' && <OverviewContent onPay={openPayModal} />}
          {activeTab === 'payments' && <PaymentHistoryTable />}
          {activeTab === 'filings' && <FilingStatusTracker />}
          {activeTab === 'documents' && <DocumentDownloads />}
          {activeTab === 'upload-docs' && <DocumentUploadTab />}
        </div>
      </div>

      <QuickPayModal
        open={payModalOpen}
        onClose={() => setPayModalOpen(false)}
        obligationId={selectedObligation}
      />
    </div>
  );
}

function OverviewContent({ onPay }: { onPay: (id: string) => void }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
      <div className="lg:col-span-2">
        <PaymentHistoryTable compact />
      </div>
      <div className="lg:col-span-1 space-y-4">
        <FilingStatusTracker compact />
        <DocumentDownloads compact />
      </div>
    </div>
  );
}